/**
 * **All-day API storage** (main app): canonical helpers for serializing picker/local `Date` values
 * to wire format and hydrating stored instants back into form/picker ranges.
 *
 * **Semantics:**
 * - **Picker `Date` → stored civil day** uses the date’s **local** calendar components (`getFullYear`,
 *   `getMonth`, `getDate`). The wire value is **UTC noon** on that civil day (`…T12:00:00.000Z`) so
 *   the backend stores the intended calendar date in every timezone.
 * - **Stored instant → form range** uses **UTC** calendar components of the stored instants (same as
 *   `allDayToLocalRange`) to build local midnight / end-of-day for display and date pickers.
 *
 * **Not** for: `YYYY-MM-DD` date-only strings (`dateOnly` helpers), or “is this all-day in the past
 * vs today” UI checks (`allDayValidation`). Do **not** derive calendar-day meaning from
 * `toISOString().split('T')[0]`.
 *
 * @see calendarDateUtils — low-level `allDayToPayloadDate` / `allDayToLocalRange` / `isLocalMidnight`
 */

import { allDayToLocalRange, allDayToPayloadDate, isLocalMidnight } from '../calendarDateUtils';

export interface SerializeAllDayRangeToIsoOptions {
  /**
   * When true, ensures `endIso` instant is strictly after `startIso` (e.g. Event API `endTime > startTime`).
   * If both map to the same UTC-noon millisecond, end is advanced by 1ms.
   */
  endInstantMustExceedStart?: boolean;
}

export interface AllDayRangeIso {
  startIso: string;
  endIso: string;
}

/**
 * Serialize a local all-day range (start/end picker dates) to ISO strings for API payloads.
 */
export function serializeAllDayRangeToIso(
  startPicker: Date,
  endPicker: Date,
  options?: SerializeAllDayRangeToIsoOptions
): AllDayRangeIso {
  const startMs = allDayToPayloadDate(startPicker).getTime();
  let endMs = allDayToPayloadDate(endPicker).getTime();
  if (options?.endInstantMustExceedStart && endMs <= startMs) {
    endMs = startMs + 1;
  }
  return {
    startIso: new Date(startMs).toISOString(),
    endIso: new Date(endMs).toISOString(),
  };
}

/**
 * Map stored all-day instants from the API to form/picker `Date` range (local midnight / EOD).
 */
export function storedAllDayInstantsToFormRange(
  startInstant: Date,
  endInstant: Date
): { startDate: Date; endDate: Date } {
  const { start, end } = allDayToLocalRange(startInstant, endInstant);
  return { startDate: start, endDate: end };
}

/**
 * Whether meeting/calendar form picks should be serialized as all-day UTC-noon storage
 * (`isAllDay` flag, or both edges at local midnight).
 */
export function shouldTreatMeetingPickAsAllDayStorage(
  isAllDay: boolean,
  start: Date,
  end: Date | null | undefined
): boolean {
  return isAllDay || (isLocalMidnight(start) && (end == null || isLocalMidnight(end)));
}

/**
 * Single picker date → UTC-noon `Date` for all-day project/task fields that expect a `Date` in the payload.
 */
export function localPickerDateToAllDayStorageUtcNoon(d: Date): Date {
  return allDayToPayloadDate(d);
}

/**
 * Single picker date → ISO string (UTC noon anchor) for all-day task fields on the wire.
 */
export function localPickerDateToAllDayStorageIso(d: Date): string {
  return allDayToPayloadDate(d).toISOString();
}
